/**
 * ピンチ・ドラッグで拡大縮小できる表示枠(2026-08-28 スマホ改修)。
 * アプリはページ全体のズームを禁止している(viewport maximum-scale=1)ため、
 * 添付プレビューはこのコンポーネントで中身だけをズームできるようにする。
 * - スマホ: 2本指ピンチで拡大縮小 / 拡大中は1本指でドラッグ移動 / ダブルタップで拡大⇔等倍
 * - PC: ホイールで拡大縮小 / 拡大中はドラッグ移動 / ダブルクリックで拡大⇔等倍
 * ZoomablePane は画像でもPDF(canvas)でも使える汎用版(2026-08-31)。
 * 2026-09-10 指示: 倍率を外(＋/−ボタン)からも変えられるようにし、いまの倍率を親へ知らせる。
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import ViewerControls, { ZOOM_MAX, ZOOM_MIN, clampZoom } from './ViewerControls';

const MIN_SCALE = ZOOM_MIN;
const MAX_SCALE = ZOOM_MAX;

export function ZoomablePane({
  children,
  style,
  contentStyle,
  scale,
  onScaleChange,
}: {
  children: ReactNode;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
  /** ＋/−ボタンなど外から指定する倍率(1=画面に収まる大きさ) */
  scale?: number;
  /** ピンチ・ホイールで倍率が変わったときに親へ知らせる */
  onScaleChange?: (next: number) => void;
}) {
  const [t, setT] = useState({ scale: scale ?? 1, x: 0, y: 0 });

  // 外から倍率が変わったら合わせる(＋/−・「全体」ボタン)
  useEffect(() => {
    if (scale == null) return;
    setT((prev) => (Math.abs(prev.scale - scale) < 0.001 ? prev : { scale, x: scale <= 1 ? 0 : prev.x, y: scale <= 1 ? 0 : prev.y }));
  }, [scale]);
  /** 現在押されているポインタ(タッチ・マウス)の位置 */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  /** ピンチ開始時の2本指間の距離と、そのときの倍率 */
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);
  const lastTap = useRef(0);

  function distance() {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return 1;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function clampScale(s: number) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
  }

  function apply(next: { scale: number; x: number; y: number }) {
    // 等倍に戻ったら位置もリセット(中身が画面外に置き去りにならないように)
    const fixed = next.scale <= 1.02 && next.scale >= 0.98 ? { scale: 1, x: 0, y: 0 } : next;
    setT(fixed);
    onScaleChange?.(clampZoom(fixed.scale));
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      pinchStart.current = { dist: distance(), scale: t.scale };
    } else if (pointers.current.size === 1) {
      // ダブルタップ(300ms以内の2回目)で拡大⇔等倍
      const now = Date.now();
      if (now - lastTap.current < 300) {
        apply(t.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 });
      }
      lastTap.current = now;
    }
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);
    if (pointers.current.size === 2 && pinchStart.current) {
      const scale = clampScale((pinchStart.current.scale * distance()) / pinchStart.current.dist);
      apply({ ...t, scale });
    } else if (pointers.current.size === 1 && t.scale > 1) {
      apply({ scale: t.scale, x: t.x + (cur.x - prev.x), y: t.y + (cur.y - prev.y) });
    }
  }

  function onPointerEnd(e: ReactPointerEvent<HTMLDivElement>) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  }

  function onWheel(e: ReactWheelEvent<HTMLDivElement>) {
    e.preventDefault();
    apply({ ...t, scale: clampScale(t.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)) });
  }

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onWheel={onWheel}
      onDoubleClick={() => apply(t.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 })}
      style={{
        // ホイール・ピンチは拡大縮小に使い、上下スクロールはしない(2026-09-10 指示)。
        // ページ送りは ◁ ▷ ボタンで行う
        touchAction: 'none',
        overflow: 'hidden',
        display: 'flex',
        width: '100%',
        cursor: t.scale > 1 ? 'grab' : 'zoom-in',
        ...style,
      }}
    >
      <div
        style={{
          margin: 'auto',
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          transformOrigin: 'center center',
          transition: pointers.current.size > 0 ? 'none' : 'transform .12s ease',
          userSelect: 'none',
          ...contentStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function ZoomableImage({
  src,
  alt,
  maxHeight = 'calc(100vh - 190px)',
}: {
  src: string;
  alt: string;
  /** '100%' を渡すと親(flexで確保された領域)の高さいっぱいに収める */
  maxHeight?: string;
}) {
  const fill = maxHeight === '100%';
  // PDFと同じように ＋/− と倍率(%)を出す(2026-09-10 指示)
  const [zoom, setZoom] = useState(1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0, flex: fill ? 1 : undefined }}>
      <ZoomablePane
        style={fill ? { height: '100%', minHeight: 0, flex: 1 } : { maxHeight }}
        contentStyle={fill ? { width: '100%', height: '100%', lineHeight: 0 } : { maxWidth: '100%', lineHeight: 0 }}
        scale={zoom}
        onScaleChange={setZoom}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={
            fill
              ? { display: 'block', width: '100%', height: '100%', objectFit: 'contain' }
              : { display: 'block', maxWidth: '100%', maxHeight, objectFit: 'contain' }
          }
        />
      </ZoomablePane>
      <ViewerControls zoom={zoom} onZoom={setZoom} />
    </div>
  );
}
